import * as Styles from "@universityofmaryland/web-styles-library";
import { ElementBuilder } from "@universityofmaryland/web-builder-library";
import { card } from "@universityofmaryland/web-elements-library/composite";
import { events } from "@universityofmaryland/web-elements-library/atomic";
import { createImageOrLinkedImage, createTextContainer, createTextWithLink } from "@universityofmaryland/web-utilities-library/elements";
import { LoadingState } from "../../states/loading.js";
import { EmptyState } from "../../states/empty.js";
import { PaginationState } from "../../states/pagination.js";
import { Announcer } from "../../states/announcer.js";
import "@universityofmaryland/web-utilities-library/network";
import { eventsFetchStrategyRange } from "../../strategies/fetch/events.js";
import "../../strategies/fetch/news.js";
import "../../strategies/fetch/experts.js";
import "../../strategies/fetch/inTheNews.js";
import "@universityofmaryland/web-elements-library/layout";
import { dispatch, eventNames } from "../../helpers/events/index.js";
import { groupEventsByDate } from "../../helpers/grouping/events.js";
import { setShadowStyles } from "../../helpers/styles/shadow.js";
const createFetchProps = (props, offset) => ({
  token: props.token,
  categories: props.categories,
  numberOfColumnsToShow: 1,
  numberOfRowsToStart: props.numberOfRowsToStart,
  getOffset: () => offset
});
const createImageConfig = (entry) => {
  const imageUrl = entry.image?.[0]?.url;
  const altText = entry.image?.[0]?.altText;
  if (!imageUrl || !altText) {
    return null;
  }
  return {
    imageUrl,
    altText,
    linkUrl: entry.url,
    linkLabel: "University of Maryland Event"
  };
};
const createAnnouncerMessage = (count, total, isLazyLoad) => {
  return isLazyLoad ? `Showing ${count} of ${total} events` : `Showing ${count} events`;
};
class GroupedFeedState {
  /**
   * Initialize state with initial styles
   *
   * @param initialStyles - Initial CSS styles
   */
  constructor(initialStyles) {
    this.stylesArray = [];
    this.shadowRoot = null;
    this.totalEntries = 0;
    this.offset = 0;
    this.lastDateHeadline = null;
    this.stylesArray.push(initialStyles);
  }
  /**
   * Add styles to the accumulated styles
   *
   * @param styles - CSS styles to add
   */
  addStyles(styles) {
    this.stylesArray.push(styles);
  }
  /**
   * Set shadow root reference for style updates
   *
   * @param shadow - Shadow root element
   */
  setShadowRoot(shadow) {
    this.shadowRoot = shadow;
  }
  /**
   * Update shadow DOM styles
   *
   * @returns Promise that resolves when styles are updated
   */
  async updateShadowStyles() {
    if (!this.shadowRoot) return;
    await setShadowStyles({
      shadowRoot: this.shadowRoot,
      styles: this.getStyles()
    });
  }
  /**
   * Get accumulated styles as single string
   *
   * @returns Combined CSS styles
   */
  getStyles() {
    return this.stylesArray.join("\n");
  }
  /**
   * Get shadow root callback for events
   *
   * @returns Callback function for shadow root
   */
  getShadowCallback() {
    return (shadow) => this.setShadowRoot(shadow);
  }
  /**
   * Get current offset
   *
   * @returns Current offset
   */
  getOffset() {
    return this.offset;
  }
  /**
   * Increment offset by count
   *
   * @param count - Number to increment by
   */
  incrementOffset(count) {
    this.offset += count;
  }
  /**
   * Get total entries
   *
   * @returns Total entries
   */
  getTotalEntries() {
    return this.totalEntries;
  }
  /**
   * Set total entries
   *
   * @param total - Total entries
   */
  setTotalEntries(total) {
    this.totalEntries = total;
  }
  /**
   * Get last date headline
   *
   * @returns Last date headline or null
   */
  getLastDateHeadline() {
    return this.lastDateHeadline;
  }
  /**
   * Set last date headline
   *
   * @param headline - Date headline
   */
  setLastDateHeadline(headline) {
    this.lastDateHeadline = headline;
  }
}
const createDateHeader = (date, state) => {
  const dateHeadline = document.createElement("p");
  dateHeadline.textContent = date;
  const headerElement = new ElementBuilder(dateHeadline).styled(Styles.element.text.decoration.ribbon).withStyles({
    element: {
      margin: `${Styles.token.spacing.lg} 0`
    }
  }).build();
  state.addStyles(headerElement.styles);
  return headerElement;
};
const createEventCard = (entry, isThemeDark) => {
  const imageData = createImageConfig(entry);
  const image = imageData && imageData.imageUrl && imageData.altText ? createImageOrLinkedImage(imageData) : null;
  return card.list({
    headline: createTextWithLink({ text: entry.title, url: entry.url }),
    text: createTextContainer({ text: entry.summary, allowHTML: true }),
    dateSign: events.sign({
      startMonth: entry.startMonth,
      startDay: entry.startDay,
      endMonth: entry.endMonth,
      endDay: entry.endDay,
      isThemeDark,
      isLargeSize: true
    }),
    eventMeta: events.meta({
      ...entry,
      isThemeDark
    }),
    image,
    isAligned: false,
    isThemeDark
  });
};
const createGroupContainer = (events2, state, isThemeDark) => {
  const entriesBuilder = new ElementBuilder("div").withClassName("umd-feed-events-grouped-entries").withStyles({
    element: {
      [`&:not(:last-of-type)`]: {
        paddingBottom: Styles.token.spacing.md,
        borderBottom: `1px solid ${isThemeDark ? Styles.token.color.gray.dark : Styles.token.color.gray.light}`
      },
      [`& + .umd-feed-events-grouped-entries`]: {
        paddingTop: Styles.token.spacing.md,
        marginTop: Styles.token.spacing.md,
        borderTop: `none`
      },
      [`& > *:not(:last-child)`]: {
        paddingBottom: Styles.token.spacing.md,
        marginBottom: Styles.token.spacing.md,
        borderBottom: `1px solid ${isThemeDark ? Styles.token.color.gray.dark : Styles.token.color.gray.light}`
      }
    }
  });
  events2.forEach((entry) => {
    const eventCard = createEventCard(entry, isThemeDark);
    entriesBuilder.withChild(eventCard);
    state.addStyles(eventCard.styles);
  });
  const groupElement = entriesBuilder.build();
  state.addStyles(groupElement.styles);
  return groupElement;
};
const renderGroupedEvents = async (container, events2, state, isThemeDark, isLazyLoad, loadMore) => {
  const grid = container.querySelector(
    "#umd-feed-events-grouped-container"
  );
  if (!grid) return;
  container.querySelector(".umd-loader-container")?.remove();
  container.querySelector(`.${Styles.layout.alignment.block.center.className}`)?.remove();
  const groupedEvents = groupEventsByDate(events2);
  let actualEventCount = 0;
  groupedEvents.forEach((group) => {
    if (group.date !== state.getLastDateHeadline()) {
      const headerElement = createDateHeader(group.date, state);
      grid.appendChild(headerElement.element);
      state.setLastDateHeadline(group.date);
    }
    const groupElement = createGroupContainer(group.events, state, isThemeDark);
    grid.appendChild(groupElement.element);
    actualEventCount += group.events.length;
  });
  state.incrementOffset(actualEventCount);
  if (isLazyLoad && state.getTotalEntries() > state.getOffset()) {
    const pagination = new PaginationState({
      totalEntries: state.getTotalEntries(),
      offset: state.getOffset(),
      isLazyLoad: true,
      callback: loadMore
    });
    const paginationElement = pagination.render(container);
    if (paginationElement) {
      state.addStyles(paginationElement.styles);
    }
  }
  await state.updateShadowStyles();
};
const renderError = async (container, state, isThemeDark) => {
  const emptyState = new EmptyState({
    message: "No events found",
    isThemeDark
  });
  emptyState.render(container);
  state.addStyles(emptyState.styles);
  const announcer = new Announcer({ message: "No events found" });
  container.appendChild(announcer.getElement());
  dispatch(container, eventNames.FEED_ERROR, {
    error: "No results found"
  });
  await state.updateShadowStyles();
};
const eventsGrouped = (props) => {
  const {
    token,
    isThemeDark = false,
    isLazyLoad = false,
    numberOfRowsToStart,
    categories
  } = props;
  const containerBuilder = new ElementBuilder("div").withClassName(
    "events-grouped-feed"
  );
  const container = containerBuilder.getElement();
  const loading = new LoadingState({ isThemeDark });
  const state = new GroupedFeedState(loading.styles);
  const layoutElement = new ElementBuilder("div").withClassName("umd-feed-events-grouped").build();
  state.addStyles(layoutElement.styles);
  const loadMore = async () => {
    const fetchProps = createFetchProps(
      { token, categories, numberOfRowsToStart },
      state.getOffset()
    );
    const variables = eventsFetchStrategyRange.composeApiVariables(fetchProps);
    const entries = await eventsFetchStrategyRange.fetchEntries(variables);
    if (entries && entries.length > 0) {
      await renderGroupedEvents(
        container,
        entries,
        state,
        isThemeDark,
        isLazyLoad,
        loadMore
      );
    }
  };
  const initialize = async () => {
    loading.show(container);
    const fetchProps = createFetchProps(
      { token, categories, numberOfRowsToStart },
      0
    );
    const variables = eventsFetchStrategyRange.composeApiVariables(fetchProps);
    const [count, entries] = await Promise.all([
      eventsFetchStrategyRange.fetchCount(variables),
      eventsFetchStrategyRange.fetchEntries(variables)
    ]);
    loading.hide();
    if (!entries || entries.length === 0) {
      await renderError(container, state, isThemeDark);
      return;
    }
    state.setTotalEntries(count || entries.length);
    layoutElement.element.setAttribute(
      "id",
      "umd-feed-events-grouped-container"
    );
    container.appendChild(layoutElement.element);
    dispatch(container, eventNames.FEED_LOADED, {
      items: entries,
      count: entries.length,
      total: state.getTotalEntries()
    });
    await renderGroupedEvents(
      container,
      entries,
      state,
      isThemeDark,
      isLazyLoad,
      loadMore
    );
    const message = createAnnouncerMessage(
      entries.length,
      state.getTotalEntries(),
      isLazyLoad
    );
    const announcer = new Announcer({ message });
    container.appendChild(announcer.getElement());
  };
  initialize();
  const model = containerBuilder.build();
  return {
    element: model.element,
    get styles() {
      return state.getStyles();
    },
    events: {
      callback: state.getShadowCallback()
    }
  };
};
export {
  eventsGrouped
};
//# sourceMappingURL=grouped.js.map
